// =============================================================================
// DomainErrorsCard — bledy wykonan z ostatnich 7 dni grupowane po domenie
// =============================================================================

import { Link } from "react-router-dom";
import { AlertTriangle, XCircle, Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useDashboardStats, type DomainErrorRow } from "@/hooks/useDashboardStats";

function formatRelativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return `${Math.floor(ms / 60_000)}min temu`;
  if (h < 24) return `${h}h temu`;
  return `${Math.floor(h / 24)}d temu`;
}

export function DomainErrorsCard() {
  const { errorsGroupedByDomain, loading } = useDashboardStats();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-400" />
          Bledy wykonan (7 dni)
        </CardTitle>
        <CardDescription>
          Padniete joby z generation_queue + publish_queue, grupowane po domenie.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading && errorsGroupedByDomain.length === 0 && (
          <p className="text-sm text-muted-foreground italic">Ladowanie...</p>
        )}
        {!loading && errorsGroupedByDomain.length === 0 && (
          <p className="text-sm text-emerald-500 italic">Brak bledow w ostatnich 7 dniach 🎉</p>
        )}
        <div className="space-y-3">
          {errorsGroupedByDomain.map((group) => (
            <div key={group.websiteName} className="rounded-md border border-red-500/25 bg-red-500/5 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-sm font-semibold text-foreground">{group.websiteName}</span>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="border-red-500/40 text-red-400 bg-red-500/10">
                    {group.count} {group.count === 1 ? "blad" : group.count < 5 ? "bledy" : "bledow"}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    ostatnio {formatRelativeTime(group.lastFailedAt)}
                  </span>
                </div>
              </div>
              <div className="space-y-1.5">
                {group.errors.slice(0, 3).map((err, i) => (
                  <ErrorRow key={i} err={err} />
                ))}
                {group.errors.length > 3 && (
                  <div className="text-[10px] text-muted-foreground/70 italic pl-4">
                    +{group.errors.length - 3} wiecej bledow dla tej domeny
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ErrorRow({ err }: { err: DomainErrorRow }) {
  const icon = err.queueType === "publish" ? Send : XCircle;
  const Icon = icon;
  const queueLabel = err.queueType === "publish" ? "publish" : "gen";

  const content = (
    <div className="flex items-start gap-2 text-[11px]">
      <Icon className="h-3 w-3 text-red-400 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-muted/60 text-muted-foreground font-mono">
            {queueLabel}
          </span>
          <span className="font-medium text-foreground truncate">
            {err.nodeTitle ?? "node bez tytulu"}
          </span>
          {err.mapName && (
            <span className="text-muted-foreground/70 text-[10px]">· {err.mapName}</span>
          )}
        </div>
        {err.errorMessage && (
          <div className="text-red-300/80 mt-0.5 text-[10px] line-clamp-2 font-mono">
            {err.errorMessage}
          </div>
        )}
      </div>
      <span className="text-[10px] text-muted-foreground/60 tabular-nums shrink-0">
        {formatRelativeTime(err.failedAt)}
      </span>
    </div>
  );

  if (err.mapId) {
    return (
      <Link
        to={`/mapy-tematyczne/${err.mapId}`}
        className="block px-2 py-1.5 rounded hover:bg-muted/30 transition-colors"
        title="Otworz mape"
      >
        {content}
      </Link>
    );
  }
  return <div className="px-2 py-1.5">{content}</div>;
}
